import { isEqual } from '@travelata-libs/utils';
import { createEventHook, type EventHookOn } from '@vueuse/core';
import { defineStore, storeToRefs } from 'pinia';
import { computed, type ComputedRef, ref, type Ref, shallowRef } from 'vue';
import { getDestinationResortIds } from '@/entities/searchCriteria';
import { useTravelataPinia } from '@/shared/composables';
import type {
  FilterGroupId,
  FiltersModel,
  FiltersModelChange,
  SearchCriteriaFilters,
  SearchFiltersSource,
  SelectedFilter,
  SerpDynamicFilterOptions,
  SerpFilterGroupId,
  SerpFilterGroupView,
  SerpFilterInteraction,
  SerpProduct,
  SerpQuickFilterView,
  SerpResultsDictionaries,
} from './types';
import { filterProducts } from '../lib/filterProducts';
import {
  clearFilterGroup,
  createEmptyFiltersModel,
  getSelectedFilters,
  isFilterGroupChanged,
  isFilterModelSelected,
  isFilterOptionSelected,
  isFiltersModelChanged,
  removeFilter,
  setBudgetFilter,
  toggleFilter,
} from '../lib/filtersModel';
import {
  findFilterGroup,
  getDynamicFilterOptions,
  getFilterGroupView,
  getFiltersGroups,
} from '../lib/getFiltersGroups';
import { getQuickFilters } from '../lib/quickFilters';
import {
  clearSearchFilterGroup,
  createEmptySearchCriteriaFilters,
  getSearchCriteriaFilters,
  isSearchCriteriaFiltersChanged,
  isSearchCriteriaFiltersSelected,
  isSearchFilterGroup,
  isSearchFilterGroupChanged,
  isSearchFilterGroupId,
  isSearchFilterGroupSelected,
  isSearchFilterOptionSelected,
  removeSearchFilter,
  toggleSearchFilter,
} from '../lib/searchFilters';
import { createEmptySerpResultsDictionaries } from '../lib/serpResultsDictionaries';

export const SERP_FILTERS_STORE_ID = 'travelata/serpFilters';

const defineSerpFiltersStore = defineStore(SERP_FILTERS_STORE_ID, () => {
  const filtersModel = ref<FiltersModel>(createEmptyFiltersModel());
  const filtersModelDraft = ref<FiltersModel>(createEmptyFiltersModel());
  const searchCriteriaDraft = ref<SearchCriteriaFilters>(createEmptySearchCriteriaFilters());
  const filtersModelChangeHook = createEventHook<FiltersModelChange>();
  const filterInteractionHook = createEventHook<SerpFilterInteraction>();

  function notifyFiltersModelChange(previous: FiltersModel, replaced: boolean): void {
    if (previous === filtersModel.value) return;

    filtersModelChangeHook.trigger({ previous, filtersModel: filtersModel.value, replaced });
  }

  function notifyFilterInteraction(groupId: SerpFilterGroupId, key: string | null, selected: boolean): void {
    filterInteractionHook.trigger({ groupId, key, selected });
  }

  function isDraftOptionSelected(groupId: SerpFilterGroupId, key: string): boolean {
    const group = findFilterGroup(groupId);

    if (!group || isSearchFilterGroup(group) || group.control === 'budget') return false;

    return isFilterOptionSelected(filtersModelDraft.value, group, key);
  }

  function setFiltersModelDraft(groupId: FilterGroupId, nextFiltersModelDraft: FiltersModel): boolean {
    const previous = filtersModelDraft.value;

    filtersModelDraft.value = nextFiltersModelDraft;

    return isFilterGroupChanged(previous, nextFiltersModelDraft, groupId);
  }

  function setSearchCriteriaDraft(nextSearchCriteriaDraft: SearchCriteriaFilters): void {
    if (isEqual(searchCriteriaDraft.value, nextSearchCriteriaDraft)) return;

    searchCriteriaDraft.value = nextSearchCriteriaDraft;
  }

  function syncDraft(criteria: SearchFiltersSource): void {
    filtersModelDraft.value = filtersModel.value;
    setSearchCriteriaDraft(getSearchCriteriaFilters(criteria));
  }

  function syncResortsDraft(criteria: SearchFiltersSource): void {
    setSearchCriteriaDraft({
      ...searchCriteriaDraft.value,
      resorts: getDestinationResortIds(criteria.destination),
    });
  }

  function toggle(groupId: SerpFilterGroupId, key: string): void {
    if (isSearchFilterGroupId(groupId)) {
      searchCriteriaDraft.value = toggleSearchFilter(searchCriteriaDraft.value, groupId, key);
      notifyFilterInteraction(groupId, key, isSearchFilterOptionSelected(searchCriteriaDraft.value, groupId, key));
      return;
    }

    if (setFiltersModelDraft(groupId, toggleFilter(filtersModelDraft.value, groupId, key))) {
      notifyFilterInteraction(groupId, key, isDraftOptionSelected(groupId, key));
    }
  }

  function setBudget(budget: FiltersModel['budget']): void {
    filtersModelDraft.value = setBudgetFilter(filtersModelDraft.value, budget);
  }

  function setResorts(resortIds: readonly number[]): void {
    searchCriteriaDraft.value = { ...searchCriteriaDraft.value, resorts: [...resortIds] };
  }

  function clearGroup(groupId: SerpFilterGroupId): void {
    if (isSearchFilterGroupId(groupId)) {
      const changed = isSearchFilterGroupSelected(searchCriteriaDraft.value, groupId);

      searchCriteriaDraft.value = clearSearchFilterGroup(searchCriteriaDraft.value, groupId);

      if (changed) notifyFilterInteraction(groupId, null, false);
      return;
    }

    if (setFiltersModelDraft(groupId, clearFilterGroup(filtersModelDraft.value, groupId))) {
      notifyFilterInteraction(groupId, null, false);
    }
  }

  function remove(groupId: SerpFilterGroupId, key: string): void {
    if (isSearchFilterGroupId(groupId)) {
      const changed = isSearchFilterOptionSelected(searchCriteriaDraft.value, groupId, key);

      searchCriteriaDraft.value = removeSearchFilter(searchCriteriaDraft.value, groupId, key);

      if (changed) notifyFilterInteraction(groupId, key, false);
      return;
    }

    if (setFiltersModelDraft(groupId, removeFilter(filtersModelDraft.value, groupId, key))) {
      notifyFilterInteraction(groupId, key, false);
    }
  }

  function reset(): void {
    filtersModelDraft.value = createEmptyFiltersModel();
    searchCriteriaDraft.value = createEmptySearchCriteriaFilters();
  }

  function apply(): void {
    const previous = filtersModel.value;

    filtersModel.value = filtersModelDraft.value;
    notifyFiltersModelChange(previous, false);
  }

  function replace(nextFiltersModel: FiltersModel): void {
    const previous = filtersModel.value;
    const nextModel = { ...nextFiltersModel };

    filtersModel.value = nextModel;
    filtersModelDraft.value = nextModel;
    notifyFiltersModelChange(previous, true);
  }

  return {
    filtersModel,
    filtersModelDraft,
    searchCriteriaDraft,
    syncDraft,
    syncResortsDraft,
    toggle,
    setBudget,
    setResorts,
    clearGroup,
    remove,
    reset,
    apply,
    replace,
    onFiltersModelChange: filtersModelChangeHook.on,
    onFilterInteraction: filterInteractionHook.on,
  };
});

export interface SerpFilters {
  filtersModel: Ref<FiltersModel>;
  filtersModelDraft: Ref<FiltersModel>;
  searchCriteriaDraft: Ref<SearchCriteriaFilters>;
  selectedCount: ComputedRef<number>;
  quickFilters: ComputedRef<SerpQuickFilterView[]>;
  filteredProducts: ComputedRef<SerpProduct[]>;
  filtersGroups: ComputedRef<SerpFilterGroupView[]>;
  getFilterGroup: (groupId: SerpFilterGroupId) => SerpFilterGroupView | null;
  selectedFilters: ComputedRef<SelectedFilter[]>;
  isDraftChanged: ComputedRef<boolean>;
  isGroupDraftChanged: (groupId: SerpFilterGroupId) => boolean;
  isDraftSelected: ComputedRef<boolean>;
  syncDraft: () => void;
  syncResortsDraft: () => void;
  toggle: (groupId: SerpFilterGroupId, key: string) => void;
  setBudget: (budget: FiltersModel['budget']) => void;
  setResorts: (resortIds: readonly number[]) => void;
  clearGroup: (groupId: SerpFilterGroupId) => void;
  remove: (groupId: SerpFilterGroupId, key: string) => void;
  reset: () => void;
  apply: () => void;
  replace: (filtersModel: FiltersModel) => void;
  onFiltersModelChange: EventHookOn<FiltersModelChange>;
  onFilterInteraction: EventHookOn<SerpFilterInteraction>;
}

const NO_PRODUCTS = shallowRef<SerpProduct[]>([]);

const NO_CRITERIA = shallowRef<SearchFiltersSource>({ destination: null });

const NO_DICTIONARIES = shallowRef<SerpResultsDictionaries>(createEmptySerpResultsDictionaries());

export function useSerpFilters(
  products: Ref<SerpProduct[]> = NO_PRODUCTS,
  criteria: Ref<SearchFiltersSource> = NO_CRITERIA,
  dictionaries: Ref<SerpResultsDictionaries> = NO_DICTIONARIES,
): SerpFilters {
  const store = defineSerpFiltersStore(useTravelataPinia());
  const { filtersModel, filtersModelDraft, searchCriteriaDraft } = storeToRefs(store);

  const dynamicOptions = computed<SerpDynamicFilterOptions>(() =>
    getDynamicFilterOptions(dictionaries.value, criteria.value.destination, criteria.value.fromCity),
  );

  const appliedSelectedFilters = computed<SelectedFilter[]>(() =>
    getSelectedFilters(filtersModel.value, getSearchCriteriaFilters(criteria.value), dynamicOptions.value),
  );

  const selectedCount = computed<number>(() => appliedSelectedFilters.value.length);
  const quickFilters = computed<SerpQuickFilterView[]>(() =>
    getQuickFilters(
      { products: products.value, filtersModel: filtersModel.value, dynamicOptions: dynamicOptions.value },
      appliedSelectedFilters.value,
    ),
  );
  const filteredProducts = computed<SerpProduct[]>(() => filterProducts(products.value, filtersModel.value));
  const filtersGroups = computed<SerpFilterGroupView[]>(() =>
    getFiltersGroups(products.value, filtersModelDraft.value, searchCriteriaDraft.value, dynamicOptions.value),
  );
  const selectedFilters = computed<SelectedFilter[]>(() =>
    getSelectedFilters(filtersModelDraft.value, searchCriteriaDraft.value, dynamicOptions.value),
  );

  const isDraftChanged = computed<boolean>(
    () =>
      isFiltersModelChanged(filtersModel.value, filtersModelDraft.value) ||
      isSearchCriteriaFiltersChanged(criteria.value, searchCriteriaDraft.value),
  );

  const isGroupDraftChanged = (groupId: SerpFilterGroupId): boolean =>
    isSearchFilterGroupId(groupId)
      ? isSearchFilterGroupChanged(criteria.value, searchCriteriaDraft.value, groupId)
      : isFilterGroupChanged(filtersModel.value, filtersModelDraft.value, groupId);

  const isDraftSelected = computed<boolean>(
    () => isFilterModelSelected(filtersModelDraft.value) || isSearchCriteriaFiltersSelected(searchCriteriaDraft.value),
  );

  const getFilterGroup = (groupId: SerpFilterGroupId): SerpFilterGroupView | null => {
    const group = findFilterGroup(groupId);

    if (!group) return null;

    return getFilterGroupView(
      { products: products.value, filtersModel: filtersModelDraft.value, dynamicOptions: dynamicOptions.value },
      searchCriteriaDraft.value,
      group,
    );
  };

  return {
    filtersModel,
    filtersModelDraft,
    searchCriteriaDraft,
    selectedCount,
    quickFilters,
    filteredProducts,
    filtersGroups,
    getFilterGroup,
    selectedFilters,
    isDraftChanged,
    isGroupDraftChanged,
    isDraftSelected,
    syncDraft: () => store.syncDraft(criteria.value),
    syncResortsDraft: () => store.syncResortsDraft(criteria.value),
    toggle: store.toggle,
    setBudget: store.setBudget,
    setResorts: store.setResorts,
    clearGroup: store.clearGroup,
    remove: store.remove,
    reset: store.reset,
    apply: store.apply,
    replace: store.replace,
    onFiltersModelChange: store.onFiltersModelChange,
    onFilterInteraction: store.onFilterInteraction,
  };
}
